// BKA app shell.
//
// Gate order: not signed in → SignInGate. Signed in but onboarding
// incomplete → the wizard (unless the creator chose "Look around BKA",
// which flips view to "app"). Fully onboarded → straight to the
// dashboard. The dashboard's four tabs are Sprint-0 placeholders; the
// wizard (Sprint 1) provisions the creator's two repos.

import { useEffect, useState } from "react";
import { useAuthStore } from "./store/authStore";
import { useOnboardingStore } from "./store/onboardingStore";
import { useNavStore, TABS } from "./store/navStore";
import { STEP } from "./lib/storage/onboarding";
import { SignInGate } from "./components/SignInGate";
import { UserChip } from "./components/UserChip";
import { HelpModal } from "./components/HelpModal";
import { Wizard } from "./components/wizard/Wizard";
import { ComposeTab } from "./components/tabs/ComposeTab";
import { LibraryTab } from "./components/tabs/LibraryTab";
import { StatsTab } from "./components/tabs/StatsTab";
import { SettingsTab } from "./components/tabs/SettingsTab";

const APPS_HOME = "https://binary-blender.com/apps";

export default function App() {
  const user = useAuthStore((s) => s.user);
  const loading = useOnboardingStore((s) => s.loading);
  const step = useOnboardingStore((s) => s.record?.step ?? STEP.NONE);
  const hydrate = useOnboardingStore((s) => s.hydrate);
  const view = useNavStore((s) => s.view);

  useEffect(() => {
    if (user) void hydrate(user.sub);
  }, [user, hydrate]);

  if (!user) return <SignInGate />;
  if (loading) return <BootSplash />;

  // Onboarding runs through first-publish (Sprint 2). Until that lands
  // a creator is never "fully onboarded", so the wizard stays the
  // default surface — but "Look around BKA" lets them into the app.
  const onboarded = step >= STEP.PUBLISHED;
  if (!onboarded && view === "wizard") return <Wizard />;

  return <Dashboard onboarded={onboarded} />;
}

function BootSplash() {
  return (
    <div className="boot-splash">
      <span className="boot-mark">BKA</span>
      <span className="boot-msg">opening your studio…</span>
      <style>{`
        .boot-splash {
          position: fixed; inset: 0;
          display: flex; flex-direction: column; gap: 8px;
          align-items: center; justify-content: center;
          background: var(--paper);
        }
        .boot-mark { font-family: var(--font-serif); font-size: 32px; font-weight: 600; color: var(--teal); letter-spacing: 0.04em; }
        .boot-msg { font-family: var(--font-mono); font-size: 11px; color: var(--ink-faint); font-style: italic; }
      `}</style>
    </div>
  );
}

function Dashboard({ onboarded }: { onboarded: boolean }) {
  const tab = useNavStore((s) => s.tab);
  const setTab = useNavStore((s) => s.setTab);
  const setView = useNavStore((s) => s.setView);
  const [help, setHelp] = useState(false);

  return (
    <div className="app-root">
      <header className="titlebar">
        <div className="titlebar-left">
          <a className="apps-link" href={APPS_HOME} title="back to binary-blender apps">← apps</a>
          <span className="wordmark">BKA</span>
        </div>

        <nav className="tabnav" aria-label="primary">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={"tab" + (tab === t.id ? " tab-active" : "")}
              aria-current={tab === t.id ? "page" : undefined}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className="titlebar-right">
          {!onboarded && (
            <button className="setup-pill" onClick={() => setView("wizard")} title="finish connecting Cloudflare + publish">
              finish setup
            </button>
          )}
          <button className="help-btn" onClick={() => setHelp(true)} title="help" aria-label="help">?</button>
          <UserChip />
        </div>
      </header>

      <main className="app-main">
        {tab === "compose" && <ComposeTab />}
        {tab === "library" && <LibraryTab />}
        {tab === "stats" && <StatsTab />}
        {tab === "settings" && <SettingsTab />}
      </main>

      {help && <HelpModal onClose={() => setHelp(false)} />}
      <Styles />
    </div>
  );
}

function Styles() {
  return (
    <style>{`
      .app-root {
        height: 100%;
        display: flex;
        flex-direction: column;
        background: var(--paper);
      }
      .titlebar {
        display: flex;
        align-items: center;
        gap: var(--space-4);
        height: 52px;
        padding: 0 var(--space-4);
        background: var(--paper-raised);
        border-bottom: 1px solid var(--line);
        flex: none;
      }
      .titlebar-left {
        display: flex; align-items: center; gap: var(--space-4);
        min-width: 220px;
      }
      .apps-link {
        font-family: var(--font-mono);
        font-size: 11px;
        color: var(--ink-faint);
      }
      .apps-link:hover { color: var(--teal); text-decoration: none; }
      .wordmark {
        font-family: var(--font-serif);
        font-size: 20px;
        font-weight: 600;
        letter-spacing: 0.04em;
        color: var(--teal);
      }
      .tabnav {
        flex: 1;
        display: flex;
        justify-content: center;
        gap: var(--space-1);
      }
      .tab {
        font-family: var(--font-sans);
        font-size: 13px;
        color: var(--ink-soft);
        background: transparent;
        border: none;
        border-radius: var(--radius-1);
        padding: 7px 14px;
        cursor: pointer;
      }
      .tab:hover { background: var(--paper-sunk); color: var(--ink); }
      .tab-active, .tab-active:hover {
        color: var(--teal);
        background: var(--teal-dim);
        font-weight: 600;
      }
      .titlebar-right {
        display: flex; align-items: center; justify-content: flex-end;
        gap: var(--space-3);
        min-width: 220px;
      }
      .setup-pill {
        font-family: var(--font-sans); font-size: 12px; font-weight: 600;
        color: var(--teal);
        background: var(--teal-dim);
        border: 1px solid transparent;
        border-radius: 999px;
        padding: 4px 12px;
        cursor: pointer;
      }
      .setup-pill:hover { border-color: var(--teal); }
      .help-btn {
        flex: none; width: 26px; height: 26px; border-radius: 50%;
        border: 1px solid var(--line); background: var(--paper-raised);
        color: var(--ink-soft); font-size: 13px; font-weight: 600; cursor: pointer;
      }
      .help-btn:hover { border-color: var(--teal); color: var(--teal); }
      .app-main {
        flex: 1;
        min-height: 0;
      }
    `}</style>
  );
}
